import { normalizeESTtoUTC, normalizeUTC } from '@/utils/date';
import type {
  ShipmentDetailedItemCustomerApi,
  ShipmentListItemCustomerApi,
  ShipmentSearchFilters,
} from '@/views/screens/Shipment/types';
import { cloneDeep } from 'es-toolkit';
import { getSearchQuoteDefaultFilters, getShipmentDefaultFilters } from '../../contants/ShipmentEnums';
import { SEARCH_VIEW_TYPES, type SearchViewType } from '../../contants/common';
import { convertDateESTToUTCToUserLocal } from '../../utilz/dates';
import { DEFAULT_STORE_OBJECT } from '../constants';
import type { DefaultStoreObject } from '../types';
import ShipmentTypes from './ShipmentTypes';

export interface ShipmentState extends Record<string, any> {
  /* Shipment */
  shipmentDetails: DefaultStoreObject<ShipmentDetailedItemCustomerApi>;
  selectedShipments: ShipmentListItemCustomerApi[];
  shipmentList: DefaultStoreObject<ShipmentListItemCustomerApi[]> & {
    totalRecords: number;
    latedelivery: number;
    latepickup: number;
    missingDocument: number;
    pickupFailed: number;
    trackingfailures: number;
    isFirstTime: boolean;
  };
  shipmentFilters: ShipmentSearchFilters;
  shipmentDashboard: {
    shipmentsByMonth?: { label: string; value: number }[];
    orderStatusData?: { label: string; value: number }[];
    transportationProvider?: { label: string; value: number }[];
    shipmentsCargoType?: { label: string; value: number }[];
    shipmentDashboard?: { label: string; value: number }[];
  };
  shipmentEditStatus: boolean;
  generateShipmentNo: DefaultStoreObject<null> & { shipmentNo: string | null };
  generateApprovalNo: DefaultStoreObject<null> & { approvalNo: string | null };
  copyShipment: DefaultStoreObject<string>;
  consolidateShipments: DefaultStoreObject<string>;
  createShipment: DefaultStoreObject<string>;
  shipmentSpotBid: DefaultStoreObject<string>;
  shipmentActiveTab: string;
  shipmentViewMode: SearchViewType;
  shipmentProductViewMode: SearchViewType;
  saveAddressBook: DefaultStoreObject<string>;
  deleteAddressBook: DefaultStoreObject<string>;
  saveProduct: DefaultStoreObject<string>;
  saveShipmentItrack: DefaultStoreObject<string>;
  endSpotBid: DefaultStoreObject<string>;
  saveShipmentAccounting: DefaultStoreObject<string>;
  deleteShipmentTenderProcess: DefaultStoreObject<string>;

  // parcel express
  branchAccNo: string;

  // PO
  poTableColumn: { label: string; value: string }[];
}

const initialState: ShipmentState = {
  /* Shipment */
  shipmentDetails: { ...DEFAULT_STORE_OBJECT, data: {} as ShipmentDetailedItemCustomerApi },
  selectedShipments: [],
  shipmentList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    totalRecords: 0,
    latedelivery: 0,
    latepickup: 0,
    missingDocument: 0,
    pickupFailed: 0,
    trackingfailures: 0,
    isFirstTime: true,
  },
  shipmentFilters: getShipmentDefaultFilters(),
  shipmentDashboard: {},
  shipmentEditStatus: false,
  generateShipmentNo: { ...DEFAULT_STORE_OBJECT, shipmentNo: null },
  generateApprovalNo: { ...DEFAULT_STORE_OBJECT, approvalNo: null },
  copyShipment: { ...DEFAULT_STORE_OBJECT }, //
  consolidateShipments: { ...DEFAULT_STORE_OBJECT },
  createShipment: { ...DEFAULT_STORE_OBJECT },
  shipmentSpotBid: { ...DEFAULT_STORE_OBJECT },
  rateShopList: { ...DEFAULT_STORE_OBJECT, data: [], havingLeastCost: false },
  endSpotBid: { ...DEFAULT_STORE_OBJECT },
  documentDetails: { ...DEFAULT_STORE_OBJECT, data: [] },
  defaultAccessorials: { ...DEFAULT_STORE_OBJECT, data: [] },
  tenderEmail: { ...DEFAULT_STORE_OBJECT, data: {} },
  tenderProcess: { ...DEFAULT_STORE_OBJECT, data: [] },
  spotBidData: { ...DEFAULT_STORE_OBJECT, data: [] },
  shipmentPartialSave: {},
  mileageCalculator: { ...DEFAULT_STORE_OBJECT, data: [] },
  customerRoutingsSettings: { ...DEFAULT_STORE_OBJECT, data: {} },
  shipmentActiveTab: '1',
  saveAddressBook: { ...DEFAULT_STORE_OBJECT, data: null },
  deleteAddressBook: { ...DEFAULT_STORE_OBJECT },
  saveProduct: { ...DEFAULT_STORE_OBJECT, data: null },
  trackingDetails: { ...DEFAULT_STORE_OBJECT, data: {} },
  sendDocumentMail: { ...DEFAULT_STORE_OBJECT },
  saveShipmentItrack: { ...DEFAULT_STORE_OBJECT },
  notificationRouting: { ...DEFAULT_STORE_OBJECT, data: [] },
  notificationDistributors: { ...DEFAULT_STORE_OBJECT, data: [] },
  saveNotificationDistributors: { ...DEFAULT_STORE_OBJECT, data: [] },
  deleteNotificationDistributors: { ...DEFAULT_STORE_OBJECT, data: [] },
  saveNotificationRouting: { ...DEFAULT_STORE_OBJECT, data: [] },
  deleteNotificationRouting: { ...DEFAULT_STORE_OBJECT, data: [] },
  addressBookFilters: {
    customerKey: undefined,
    searchId: undefined,
    name: undefined,
    address: undefined,
    state: undefined,
    countryCode: undefined,
    city: undefined,
    zip: undefined,
    pageSize: 10,
    pageNumber: 1,
  },
  customerAddressBooks: { ...DEFAULT_STORE_OBJECT, data: [], totalRecords: 0 },
  shipmentCustomerAccessorials: { ...DEFAULT_STORE_OBJECT, data: [] },
  createShipmentQuote: { ...DEFAULT_STORE_OBJECT },
  saveShipmentAccounting: { ...DEFAULT_STORE_OBJECT },
  createShipmentFromQuote: { ...DEFAULT_STORE_OBJECT },
  getMilageByStops: {
    ...DEFAULT_STORE_OBJECT,
    totalMilageInMiles: 0,
    totalMilageInKm: 0,
  },
  parcelExpressRates: { ...DEFAULT_STORE_OBJECT, data: [] },
  createParcelExpress: { ...DEFAULT_STORE_OBJECT, data: {} },
  validateAndApprovers: { ...DEFAULT_STORE_OBJECT, data: {} },
  addBidder: { ...DEFAULT_STORE_OBJECT, data: {} },
  createChildShipment: { ...DEFAULT_STORE_OBJECT, data: {} },
  sendApprovalRequest: { ...DEFAULT_STORE_OBJECT, data: {} },
  documentUploadData: [],
  assignCarrier: { ...DEFAULT_STORE_OBJECT, data: {} },
  addRateEntry: { ...DEFAULT_STORE_OBJECT, data: {} },
  validateRateEntry: { ...DEFAULT_STORE_OBJECT, data: {} },
  removeRateEntry: { ...DEFAULT_STORE_OBJECT, data: {} },
  masterChildDetails: { ...DEFAULT_STORE_OBJECT, data: [] },
  updateRateEntry: { ...DEFAULT_STORE_OBJECT, data: {} },
  partialToShipment: { ...DEFAULT_STORE_OBJECT, data: {} },
  branchAddress: { ...DEFAULT_STORE_OBJECT, data: {} },
  shipmentsStops: { ...DEFAULT_STORE_OBJECT, data: [] },
  parcelExpressDashboardData: {
    totalSpend: { ...DEFAULT_STORE_OBJECT, data: {} },
    totalSpendByCarriers: { ...DEFAULT_STORE_OBJECT, data: [] },
    byMonths: { ...DEFAULT_STORE_OBJECT, data: [] },
  },
  shipmentViewMode: SEARCH_VIEW_TYPES.CARD,
  shipmentProductViewMode: SEARCH_VIEW_TYPES.CARD,
  tableViewColumns: { ...DEFAULT_STORE_OBJECT, data: [] },
  saveTableViewColumns: { ...DEFAULT_STORE_OBJECT, data: [] },
  shipmentProductList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    totalRecords: 0,
    PageSize: 10,
    PageNumber: 1,
  },
  shipmentProductFullList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  productPartialSave: { ...DEFAULT_STORE_OBJECT },
  updateProductFreightClass: { ...DEFAULT_STORE_OBJECT },
  updateShipmentBOLCarrier: { ...DEFAULT_STORE_OBJECT },
  getAdvancePlanningNoticeEmails: { ...DEFAULT_STORE_OBJECT, data: [] },
  shipmentEditRateShopModal: false,
  selectedRateShopIndex: false,
  shipmentVendorData: {
    shipment: {},
    po: {},
  },
  deleteShipmentTenderProcess: { ...DEFAULT_STORE_OBJECT },
  cargoTypes: DEFAULT_STORE_OBJECT,
  cargoSpecificAccessorials: { ...DEFAULT_STORE_OBJECT, data: [] },

  // parcel express
  branchAccNo: '',

  // quote
  quoteSearchList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    awarded: 0,
    cancelled: 0,
    internalReview: 0,
    openForBid: 0,
    readyForAward: 0,
    totalRecords: 0,
    isFirstTime: true,
  },
  selectedQuotes: [],
  spotQuoteBidders: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  searchableSpotQuoteBidders: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  spotQuoteCollaborators: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  searchQuoteFilters: getSearchQuoteDefaultFilters(),

  // PO
  poTableColumn: [],
};

// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const ShipmentReducer = (state = initialState, { type, payload }: any): ShipmentState => {
  switch (type) {
    case ShipmentTypes.CLEAR_SHIPEMENT_DETAILS: {
      return {
        ...state,
        shipmentDetails: { ...DEFAULT_STORE_OBJECT },
      };
    }

    case ShipmentTypes.SET_BRANCH_ACC_NUMBER: {
      return {
        ...state,
        branchAccNo: payload,
      };
    }
    case ShipmentTypes.SET_SHIPMENT_VENDOR_DATA: {
      return {
        ...state,
        shipmentVendorData: {
          ...state.shipmentVendorData,
          ...payload,
        },
      };
    }
    case ShipmentTypes.SET_PO_TABLE_COLUMN: {
      return {
        ...state,
        poTableColumn: payload,
      };
    }
    case ShipmentTypes.DOCUMENT_UPLOAD_LIST: {
      return {
        ...state,
        documentUploadData: payload,
      };
    }

    case ShipmentTypes.GET_SHIPMENT_LIST_PROGRESS: {
      return {
        ...state,
        shipmentList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.shipmentList,
          progressing: true,
          isFirstTime: false,
        },
      };
    }

    case ShipmentTypes.GET_SEARCH_QUOTE_LIST_PROGRESS: {
      return {
        ...state,
        quoteSearchList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.quoteSearchList,
          progressing: true,
          isFirstTime: false,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_LIST_SUCCESS: {
      let selectedShipments = cloneDeep(state.selectedShipments);
      if (
        state.selectedShipments?.length === state.shipmentList.data?.length &&
        state.selectedShipments?.length !== 0 // To fixe select all checkbox issue
      ) {
        // @ts-ignore
        payload.data.shipmentData.forEach(({ shipmentKey }) => selectedShipments.push(shipmentKey));
      } else if (state.shipmentFilters.PageNumber === 1) {
        selectedShipments = []; // To solve shipment headers issue
      }
      return {
        ...state,
        selectedShipments,
        shipmentList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: [
            ...(state.shipmentList.data || []),
            // @ts-ignore
            ...payload.data.shipmentData.map((element) => ({
              ...element,
              expDeliveryDate: convertDateESTToUTCToUserLocal(element.expDeliveryDate),
              expShipDate: convertDateESTToUTCToUserLocal(element.expShipDate),
              createdOn: convertDateESTToUTCToUserLocal(element.createdOn),
              shipmentDate: convertDateESTToUTCToUserLocal(element.shipmentDate),
              expBidTime: normalizeUTC(element.expBidTime, 'MM/dd/yyyy h:mm a'),
            })),
          ],
          totalRecords: payload.data.shipmentCount,
          latedelivery: payload.data.latedelivery,
          latepickup: payload.data.latepickup,
          missingDocument: payload.data.missingDocument,
          pickupFailed: payload.data.pickupFailed,
          trackingfailures: payload.data.trackingfailures,
          isFirstTime: false,
        },
      };
    }

    case ShipmentTypes.GET_SEARCH_QUOTE_LIST_SUCCESS: {
      const selectedQuotes = cloneDeep(state.selectedQuotes);

      return {
        ...state,
        selectedQuotes,
        quoteSearchList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: [...payload.data.spotBidsData],
          awarded: payload.data.awarded,
          cancelled: payload.data.cancelled,
          internalReview: payload.data.internalReview,
          openForBid: payload.data.openForBid,
          readyForAward: payload.data.readyForAward,
          totalRecords: payload.data.totalRecords,
          isFirstTime: false,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_LIST_ERROR: {
      return {
        ...state,
        shipmentList: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.shipmentList.data || [])],
          isFirstTime: false,
        },
      };
    }

    case ShipmentTypes.REFRESH_SHIPMENT_LIST_PROGRESS: {
      return {
        ...state,
      };
    }

    case ShipmentTypes.REFRESH_SHIPMENT_LIST_SUCCESS: {
      return {
        ...state,
        shipmentList: {
          ...state.shipmentList,
          data: [
            // @ts-ignore
            ...payload.data.shipmentData.map((element) => ({
              ...element,
              expDeliveryDate: convertDateESTToUTCToUserLocal(element.expDeliveryDate),
              expShipDate: convertDateESTToUTCToUserLocal(element.expShipDate),
              createdOn: convertDateESTToUTCToUserLocal(element.createdOn),
              shipmentDate: convertDateESTToUTCToUserLocal(element.shipmentDate),
              expBidTime: normalizeUTC(element.expBidTime, 'MM/dd/yyyy h:mm a'),
            })),
          ],
          isFirstTime: false,
        },
      };
    }

    case ShipmentTypes.REFRESH_SHIPMENT_LIST_ERROR: {
      return {
        ...state,
      };
    }

    case ShipmentTypes.SET_SHIPMENT_LIST: {
      return {
        ...state,
        shipmentList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.shipmentList,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_DETAILS_PROGRESS: {
      return {
        ...state,
        shipmentDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
        // to reset shipment docs list
        documentDetails: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
        },
        tenderProcess: {
          ...DEFAULT_STORE_OBJECT,
          data: null,
        },
        spotBidData: { ...DEFAULT_STORE_OBJECT, data: [] },
        rateShopList: { ...DEFAULT_STORE_OBJECT, data: [] },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_DETAILS_SUCCESS: {
      const rateShop = !payload.skipRateShop
        ? {
            rateShopList: {
              ...DEFAULT_STORE_OBJECT,
              success: true,
              data: payload.data.rateShopList,
            },
          }
        : {};

      let selectedRateShopIndex = false;
      if (payload.data.rateShopList.length > 0) {
        selectedRateShopIndex = payload.data?.rateShopList?.findIndex(
          // @ts-ignore
          ({ rateShopKey }) => rateShopKey === payload.data.rateShopKey,
        );
      }

      return {
        ...state,
        shipmentDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
        ...rateShop,
        selectedRateShopIndex,
      };
    }

    case ShipmentTypes.GET_SHIPMENT_DETAILS_ERROR: {
      return {
        ...state,
        shipmentDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SET_SHIPMENT_BY_KEY: {
      const shipmentData = cloneDeep(state?.shipmentDetails?.data);
      if (shipmentData) {
        // @ts-ignore
        shipmentData[payload.key] = payload.data;
      }
      return {
        ...state,
        shipmentDetails: {
          ...state.shipmentDetails,
          data: shipmentData,
        },
      };
    }

    case ShipmentTypes.SET_SHIPMENT_DATA: {
      return {
        ...state,
        shipmentDetails: {
          ...state.shipmentDetails,
          data: payload,
        },
      };
    }

    case ShipmentTypes.SET_SEARCH_QUOTE_LIST: {
      return {
        ...state,
        quoteSearchList: {
          ...DEFAULT_STORE_OBJECT,
          // totalRecords: 0,
          ...state.quoteSearchList,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SET_SHIPMEN_DETAILS: {
      return {
        ...state,
        shipmentDetails: {
          ...state.shipmentDetails,
          data: payload,
        },
      };
    }

    case ShipmentTypes.SET_SHIPMENT_FILTERS: {
      return {
        ...state,
        shipmentFilters: {
          ...state.shipmentFilters,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SET_SEARCH_QUOTE_FILTERS: {
      return {
        ...state,
        searchQuoteFilters: {
          ...state.searchQuoteFilters,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_DASHBOARD_PROGRESS: {
      return {
        ...state,
        shipmentDashboard: {
          ...state.shipmentDashboard,
          [payload.fieldType]: {
            ...DEFAULT_STORE_OBJECT,
            progressing: true,
            data: null,
          },
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_DASHBOARD_SUCCESS: {
      return {
        ...state,
        shipmentDashboard: {
          ...state.shipmentDashboard,
          [payload.fieldType]: {
            ...DEFAULT_STORE_OBJECT,
            success: true,
            data: payload.data,
          },
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_DASHBOARD_ERROR: {
      return {
        ...state,
        shipmentDashboard: {
          ...state.shipmentDashboard,
          [payload.fieldType]: {
            ...DEFAULT_STORE_OBJECT,
            error: true,
            message: payload.message,
            data: null,
          },
        },
      };
    }

    case ShipmentTypes.SET_SHIPMEN_EDIT_STATUS: {
      return {
        ...state,
        shipmentEditStatus: payload,
      };
    }

    case ShipmentTypes.GENERATE_SHIPMENT_NUMBER_PROGRESS: {
      return {
        ...state,
        generateShipmentNo: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          shipmentNo: null,
        },
      };
    }
    case ShipmentTypes.GENERATE_SHIPMENT_NUMBER_SUCCESS: {
      return {
        ...state,
        generateShipmentNo: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GENERATE_SHIPMENT_NUMBER_ERROR: {
      return {
        ...state,
        generateShipmentNo: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          shipmentNo: false,
        },
      };
    }

    case ShipmentTypes.GENERATE_APPROVAL_NUMBER_PROGRESS: {
      return {
        ...state,
        generateApprovalNo: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          approvalNo: null,
        },
      };
    }
    case ShipmentTypes.GENERATE_APPROVAL_NUMBER_SUCCESS: {
      return {
        ...state,
        generateApprovalNo: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GENERATE_APPROVAL_NUMBER_ERROR: {
      return {
        ...state,
        generateApprovalNo: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          approvalNo: false,
        },
      };
    }

    case ShipmentTypes.SET_SELECTED_SHIPMENTS: {
      return {
        ...state,
        selectedShipments: payload,
      };
    }

    case ShipmentTypes.COPY_SHIPMENTS_PROGRESS: {
      return {
        ...state,
        copyShipment: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: null,
        },
      };
    }

    case ShipmentTypes.COPY_SHIPMENTS_SUCCESS: {
      return {
        ...state,
        copyShipment: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.COPY_SHIPMENTS_ERROR: {
      return {
        ...state,
        copyShipment: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.CONSOLIDATE_SHIPMENTS_PROGRESS: {
      return {
        ...state,
        consolidateShipments: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: null,
        },
      };
    }

    case ShipmentTypes.CONSOLIDATE_SHIPMENTS_SUCCESS: {
      return {
        ...state,
        consolidateShipments: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.CONSOLIDATE_SHIPMENTS_ERROR: {
      return {
        ...state,
        consolidateShipments: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.CREATE_SHIPMENT_PROGRESS: {
      return {
        ...state,
        createShipment: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: null,
        },
      };
    }

    case ShipmentTypes.CREATE_SHIPMENT_SUCCESS: {
      return {
        ...state,
        createShipment: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.CREATE_SHIPMENT_ERROR: {
      return {
        ...state,
        createShipment: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SHIPMENT_SPOTBID_PROGRESS: {
      return {
        ...state,
        shipmentSpotBid: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: null,
        },
      };
    }

    case ShipmentTypes.SHIPMENT_SPOTBID_SUCCESS: {
      return {
        ...state,
        shipmentSpotBid: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SHIPMENT_SPOTBID_ERROR: {
      return {
        ...state,
        shipmentSpotBid: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SHIPMENT_ADD_BIDDER_PROGRESS: {
      return {
        ...state,
        addBidder: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.SHIPMENT_ADD_BIDDER_SUCCESS: {
      return {
        ...state,
        addBidder: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.SHIPMENT_ADD_BIDDER_ERROR: {
      return {
        ...state,
        addBidder: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_RATE_SHOP_LIST_PROGRESS: {
      return {
        ...state,
        rateShopList: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          havingLeastCost: payload.havingLeastCost,
          data: state?.rateShopList?.data ? state?.rateShopList?.data : [],
        },
      };
    }

    case ShipmentTypes.GET_RATE_SHOP_LIST_SUCCESS: {
      const shipmentDetails = state.shipmentDetails;
      if (payload.replaceShipment && shipmentDetails.data) {
        // @ts-ignore
        shipmentDetails.data.rateShopList = payload.data;
      }
      return {
        ...state,
        rateShopList: {
          ...state.rateShopList,
          progressing: false,
          success: true,
          ...(payload.forceUpdate ? { data: payload.data } : {}),
        },
        shipmentDetails,
      };
    }

    case ShipmentTypes.GET_RATE_SHOP_LIST_ERROR: {
      return {
        ...state,
        rateShopList: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: payload.skipReset ? state.rateShopList.data : [],
        },
      };
    }
    case ShipmentTypes.RESET_RATE_SHOP_LIST: {
      return {
        ...state,
        rateShopList: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          ...payload,
        },
      };
    }

    case ShipmentTypes.END_SPOT_BID_PROGRESS: {
      return {
        ...state,
        endSpotBid: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case ShipmentTypes.END_SPOT_BID_SUCCESS: {
      return {
        ...state,
        endSpotBid: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.END_SPOT_BID_ERROR: {
      return {
        ...state,
        endSpotBid: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_DOCUMENTS_PROGRESS: {
      return {
        ...state,
        documentDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_DOCUMENTS_SUCCESS: {
      return {
        ...state,
        documentDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_DOCUMENTS_ERROR: {
      return {
        ...state,
        documentDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_DEFAULT_ACCESSORIALS_PROGRESS: {
      return {
        ...state,
        defaultAccessorials: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_DEFAULT_ACCESSORIALS_SUCCESS: {
      return {
        ...state,
        defaultAccessorials: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_DEFAULT_ACCESSORIALS_ERROR: {
      return {
        ...state,
        defaultAccessorials: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_TENDER_EMAIL_PROGRESS: {
      return {
        ...state,
        tenderEmail: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.GET_TENDER_EMAIL_SUCCESS: {
      return {
        ...state,
        tenderEmail: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }

    case ShipmentTypes.GET_TENDER_EMAIL_ERROR: {
      return {
        ...state,
        tenderEmail: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: {},
        },
      };
    }

    case ShipmentTypes.TENDER_PROCESS_PROGRESS: {
      return {
        ...state,
        tenderProcess: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.TENDER_PROCESS_SUCCESS: {
      return {
        ...state,
        tenderProcess: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }

    case ShipmentTypes.TENDER_PROCESS_ERROR: {
      return {
        ...state,
        tenderProcess: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_SPOT_BID_DATA_PROGRESS: {
      return {
        ...state,
        spotBidData: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_SPOT_BID_DATA_SUCCESS: {
      return {
        ...state,
        spotBidData: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }

    case ShipmentTypes.GET_SPOT_BID_DATA_ERROR: {
      return {
        ...state,
        spotBidData: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case ShipmentTypes.SHIPMENT_PARTIAL_SAVE: {
      return {
        ...state,
        shipmentPartialSave: {
          ...payload,
        },
      };
    }

    case ShipmentTypes.MILEAGE_CALCULATOR_PROGRESS: {
      return {
        ...state,
        mileageCalculator: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.MILEAGE_CALCULATOR_SUCCESS: {
      return {
        ...state,
        mileageCalculator: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }

    case ShipmentTypes.MILEAGE_CALCULATOR_ERROR: {
      return {
        ...state,
        mileageCalculator: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_CUSTOMER_ROUTINGS_SETTINGS_PROGRESS: {
      return {
        ...state,
        customerRoutingsSettings: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.GET_CUSTOMER_ROUTINGS_SETTINGS_SUCCESS: {
      return {
        ...state,
        customerRoutingsSettings: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }

    case ShipmentTypes.GET_CUSTOMER_ROUTINGS_SETTINGS_ERROR: {
      return {
        ...state,
        customerRoutingsSettings: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: {},
        },
      };
    }

    case ShipmentTypes.SET_SHIPMENT_DETAILS_ACTIVE_TAB: {
      return {
        ...state,
        shipmentActiveTab: payload,
      };
    }

    case ShipmentTypes.SAVE_ADDRESS_BOOK_PROGRESS: {
      return {
        ...state,
        saveAddressBook: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload,
        },
      };
    }
    case ShipmentTypes.SAVE_ADDRESS_BOOK_SUCCESS: {
      return {
        ...state,
        saveAddressBook: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }
    case ShipmentTypes.SAVE_ADDRESS_BOOK_ERROR: {
      return {
        ...state,
        saveAddressBook: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.DELETE_ADDRESS_BOOK_PROGRESS: {
      return {
        ...state,
        deleteAddressBook: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload,
        },
      };
    }
    case ShipmentTypes.DELETE_ADDRESS_BOOK_SUCCESS: {
      return {
        ...state,
        deleteAddressBook: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }
    case ShipmentTypes.DELETE_ADDRESS_BOOK_ERROR: {
      return {
        ...state,
        deleteAddressBook: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SAVE_PRODUCT_PROGRESS: {
      return {
        ...state,
        saveProduct: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload,
        },
      };
    }

    case ShipmentTypes.SAVE_PRODUCT_SUCCESS: {
      return {
        ...state,
        saveProduct: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }

    case ShipmentTypes.SAVE_PRODUCT_ERROR: {
      return {
        ...state,
        saveProduct: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_TRACKING_DETAILS_PROGRESS: {
      return {
        ...state,
        trackingDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_TRACKING_DETAILS_SUCCESS: {
      return {
        ...state,
        trackingDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }

    case ShipmentTypes.GET_TRACKING_DETAILS_ERROR: {
      return {
        ...state,
        trackingDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SEND_DOCUMENT_MAIL_PROGRESS: {
      return {
        ...state,
        sendDocumentMail: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case ShipmentTypes.SEND_DOCUMENT_MAIL_SUCCESS: {
      return {
        ...state,
        sendDocumentMail: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }

    case ShipmentTypes.SEND_DOCUMENT_MAIL_ERROR: {
      return {
        ...state,
        sendDocumentMail: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SAVE_SHIPMENT_ITRACK_PROGRESS: {
      return {
        ...state,
        saveShipmentItrack: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case ShipmentTypes.SAVE_SHIPMENT_ITRACK_SUCCESS: {
      return {
        ...state,
        saveShipmentItrack: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }

    case ShipmentTypes.SAVE_SHIPMENT_ITRACK_ERROR: {
      return {
        ...state,
        saveShipmentItrack: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_NOTIFICATION_ROUTING_PROGRESS: {
      return {
        ...state,
        notificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_NOTIFICATION_ROUTING_SUCCESS: {
      return {
        ...state,
        notificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_NOTIFICATION_ROUTING_ERROR: {
      return {
        ...state,
        notificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_NOTIFICATION_DISTRIBUTORS_PROGRESS: {
      return {
        ...state,
        notificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_NOTIFICATION_DISTRIBUTORS_SUCCESS: {
      return {
        ...state,
        notificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_NOTIFICATION_DISTRIBUTORS_ERROR: {
      return {
        ...state,
        notificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SAVE_NOTIFICATION_DISTRIBUTORS_PROGRESS: {
      return {
        ...state,
        saveNotificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case ShipmentTypes.SAVE_NOTIFICATION_DISTRIBUTORS_SUCCESS: {
      return {
        ...state,
        saveNotificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SAVE_NOTIFICATION_DISTRIBUTORS_ERROR: {
      return {
        ...state,
        saveNotificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.DELETE_NOTIFICATION_DISTRIBUTORS_PROGRESS: {
      return {
        ...state,
        deleteNotificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case ShipmentTypes.DELETE_NOTIFICATION_DISTRIBUTORS_SUCCESS: {
      return {
        ...state,
        deleteNotificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.DELETE_NOTIFICATION_DISTRIBUTORS_ERROR: {
      return {
        ...state,
        deleteNotificationDistributors: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SAVE_NOTIFICATION_ROUTING_PROGRESS: {
      return {
        ...state,
        saveNotificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case ShipmentTypes.SAVE_NOTIFICATION_ROUTING_SUCCESS: {
      return {
        ...state,
        saveNotificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.SAVE_NOTIFICATION_ROUTING_ERROR: {
      return {
        ...state,
        saveNotificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.DELETE_NOTIFICATION_ROUTING_PROGRESS: {
      return {
        ...state,
        deleteNotificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case ShipmentTypes.DELETE_NOTIFICATION_ROUTING_SUCCESS: {
      return {
        ...state,
        deleteNotificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.DELETE_NOTIFICATION_ROUTING_ERROR: {
      return {
        ...state,
        deleteNotificationRouting: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SET_SHIPMENT_ADDRESS_BOOK_FILTER: {
      return {
        ...state,
        addressBookFilters: {
          ...state.addressBookFilters,
          ...payload,
          dataReset: false,
        },
        ...(payload.dataReset === true
          ? {
              customerAddressBooks: {
                ...state.customerAddressBooks,
                data: [],
                totalRecords: 0,
              },
            }
          : {}),
      };
    }

    case ShipmentTypes.SEARCH_CUSTOMER_ADDRESS_BOOK_PROGRESS: {
      return {
        ...state,
        customerAddressBooks: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: state.customerAddressBooks.data,
          totalRecords: state.customerAddressBooks.totalRecords,
        },
      };
    }

    case ShipmentTypes.UPDATE_CUSTOMER_ADDRESS_BOOK: {
      const targetBookIndex = state?.customerAddressBooks?.data?.findIndex(
        // @ts-ignore
        (item) => item.searchId === payload.data.searchId,
      );
      if (targetBookIndex !== -1) {
        const updatedData = [
          ...(state.customerAddressBooks?.data || []).slice(0, targetBookIndex),
          { ...payload.data, editMode: false },
          ...(state.customerAddressBooks?.data || []).slice(targetBookIndex + 1),
        ];
        return {
          ...state,
          customerAddressBooks: {
            ...state?.customerAddressBooks,
            data: updatedData,
          },
        };
      }

      return {
        ...state,
      };
    }
    case ShipmentTypes.SEARCH_CUSTOMER_ADDRESS_BOOK_SUCCESS: {
      return {
        ...state,
        customerAddressBooks: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: [...state.customerAddressBooks.data, ...payload.data],
          totalRecords: payload.totalRecords,
        },
      };
    }
    case ShipmentTypes.SEARCH_CUSTOMER_ADDRESS_BOOK_ERROR: {
      return {
        ...state,
        customerAddressBooks: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SET_CUSTOMER_ADDRESS_BOOK: {
      return {
        ...state,
        customerAddressBooks: {
          ...state.customerAddressBooks,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_CUSTOMER_ACCESSORIALS_PROGRESS: {
      return {
        ...state,
        shipmentCustomerAccessorials: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENT_CUSTOMER_ACCESSORIALS_SUCCESS: {
      return {
        ...state,
        shipmentCustomerAccessorials: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENT_CUSTOMER_ACCESSORIALS_ERROR: {
      return {
        ...state,
        shipmentCustomerAccessorials: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case ShipmentTypes.CREATE_SHIPMENT_QUOTE_PROGRESS: {
      return {
        ...state,
        createShipmentQuote: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.CREATE_SHIPMENT_QUOTE_SUCCESS: {
      return {
        ...state,
        createShipmentQuote: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }
    case ShipmentTypes.CREATE_SHIPMENT_QUOTE_ERROR: {
      return {
        ...state,
        createShipmentQuote: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: {},
        },
      };
    }
    case ShipmentTypes.RESET_CREATE_SHIPMENT_QUOTE: {
      return {
        ...state,
        createShipmentQuote: {
          ...DEFAULT_STORE_OBJECT,
        },
      };
    }

    case ShipmentTypes.SAVE_SHIPMENT_ACCOUNTING_PARTIAL_PROGRESS: {
      return {
        ...state,
        saveShipmentAccounting: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: null,
        },
      };
    }
    case ShipmentTypes.SAVE_SHIPMENT_ACCOUNTING_PARTIAL_SUCCESS: {
      return {
        ...state,
        saveShipmentAccounting: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }
    case ShipmentTypes.SAVE_SHIPMENT_ACCOUNTING_PARTIAL_ERROR: {
      return {
        ...state,
        saveShipmentAccounting: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: {},
        },
      };
    }

    case ShipmentTypes.CREATE_SHIPMENT_FROM_QUOTE_PROGRESS: {
      return {
        ...state,
        createShipmentFromQuote: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.CREATE_SHIPMENT_FROM_QUOTE_SUCCESS: {
      return {
        ...state,
        createShipmentFromQuote: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
        },
      };
    }
    case ShipmentTypes.CREATE_SHIPMENT_FROM_QUOTE_ERROR: {
      return {
        ...state,
        createShipmentFromQuote: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: {},
        },
      };
    }

    case ShipmentTypes.GET_MILAGE_BY_STOPS_PROGRESS: {
      return {
        ...state,
        getMilageByStops: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case ShipmentTypes.GET_MILAGE_BY_STOPS_SUCCESS: {
      return {
        ...state,
        getMilageByStops: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          totalMilageInMiles: 0,
          totalMilageInKm: 0,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GET_MILAGE_BY_STOPS_ERROR: {
      return {
        ...state,
        getMilageByStops: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          totalMilageInMiles: 0,
          totalMilageInKm: 0,
        },
      };
    }

    case ShipmentTypes.GET_PARCEL_EXPRESS_RATES_PROGRESS: {
      return {
        ...state,
        parcelExpressRates: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case ShipmentTypes.GET_PARCEL_EXPRESS_RATES_SUCCESS: {
      return {
        ...state,
        parcelExpressRates: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GET_PARCEL_EXPRESS_RATES_ERROR: {
      return {
        ...state,
        parcelExpressRates: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case ShipmentTypes.CREATE_PARCEL_EXPRESS_SHIPPING_LABEL_PROGRESS: {
      return {
        ...state,
        createParcelExpress: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
        parcelExpressRates: {
          ...state.parcelExpressRates,
          success: false,
          error: false,
          progressing: false,
        },
      };
    }
    case ShipmentTypes.CREATE_PARCEL_EXPRESS_SHIPPING_LABEL_SUCCESS: {
      return {
        ...state,
        createParcelExpress: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.CREATE_PARCEL_EXPRESS_SHIPPING_LABEL_ERROR: {
      return {
        ...state,
        createParcelExpress: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.RESET_CREATE_PARCEL_EXPRESS_SHIPPING_LABEL: {
      return {
        ...state,
        createParcelExpress: {
          ...DEFAULT_STORE_OBJECT,
          data: {},
        },
      };
    }

    case ShipmentTypes.VALIDATE_AND_GET_APPROVERS_LIST_PROGRESS: {
      return {
        ...state,
        validateAndApprovers: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.VALIDATE_AND_GET_APPROVERS_LIST_SUCCESS: {
      return {
        ...state,
        validateAndApprovers: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.VALIDATE_AND_GET_APPROVERS_LIST_ERROR: {
      return {
        ...state,
        validateAndApprovers: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.CREATE_CHILD_SHIPMENT_PROGRESS: {
      return {
        ...state,
        createChildShipment: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.CREATE_CHILD_SHIPMENT_SUCCESS: {
      return {
        ...state,
        createChildShipment: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.CREATE_CHILD_SHIPMENT_ERROR: {
      return {
        ...state,
        createChildShipment: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.SEND_APPROVAL_REQUEST_PROGRESS: {
      return {
        ...state,
        sendApprovalRequest: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.SEND_APPROVAL_REQUEST_SUCCESS: {
      return {
        ...state,
        sendApprovalRequest: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.SEND_APPROVAL_REQUEST_ERROR: {
      return {
        ...state,
        sendApprovalRequest: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.SET_APPROVERS_LIST: {
      return {
        ...state,
        sendApprovalRequest: {
          data: {
            ...state.sendApprovalRequest.data,
            approvalList: payload,
          },
        },
      };
    }

    case ShipmentTypes.ASSIGN_CARRIER_PROGRESS: {
      return {
        ...state,
        assignCarrier: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.ASSIGN_CARRIER_SUCCESS: {
      return {
        ...state,
        assignCarrier: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.ASSIGN_CARRIER_ERROR: {
      return {
        ...state,
        assignCarrier: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.ADD_RATE_ENTRY_PROGRESS: {
      return {
        ...state,
        addRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.ADD_RATE_ENTRY_SUCCESS: {
      return {
        ...state,
        addRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.ADD_RATE_ENTRY_ERROR: {
      return {
        ...state,
        addRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.VALIDATE_RATE_ENTRY_PROGRESS: {
      return {
        ...state,
        validateRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.VALIDATE_RATE_ENTRY_SUCCESS: {
      return {
        ...state,
        validateRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.VALIDATE_RATE_ENTRY_ERROR: {
      return {
        ...state,
        validateRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.REMOVE_RATE_ENTRY_PROGRESS: {
      return {
        ...state,
        removeRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.REMOVE_RATE_ENTRY_SUCCESS: {
      return {
        ...state,
        removeRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.REMOVE_RATE_ENTRY_ERROR: {
      return {
        ...state,
        removeRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.GET_MASTER_CHILD_SHIPMENT_DETAILS_PROGRESS: {
      return {
        ...state,
        masterChildDetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case ShipmentTypes.GET_MASTER_CHILD_SHIPMENT_DETAILS_SUCCESS: {
      return {
        ...state,
        masterChildDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GET_MASTER_CHILD_SHIPMENT_DETAILS_ERROR: {
      return {
        ...state,
        masterChildDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.UPDATE_RATE_ENTRY_PROGRESS: {
      return {
        ...state,
        updateRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.UPDATE_RATE_ENTRY_SUCCESS: {
      return {
        ...state,
        updateRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.UPDATE_RATE_ENTRY_ERROR: {
      return {
        ...state,
        updateRateEntry: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_BRANCH_ADDRESS_PROGRESS: {
      return {
        ...state,
        branchAddress: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENT_BRANCH_ADDRESS_SUCCESS: {
      return {
        ...state,
        branchAddress: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENT_BRANCH_ADDRESS_ERROR: {
      return {
        ...state,
        branchAddress: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.CONVERT_PARTIAL_TO_SHIPMENT_PROGRESS: {
      return {
        ...state,
        partialToShipment: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: {},
        },
      };
    }
    case ShipmentTypes.CONVERT_PARTIAL_TO_SHIPMENT_SUCCESS: {
      return {
        ...state,
        partialToShipment: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.CONVERT_PARTIAL_TO_SHIPMENT_ERROR: {
      return {
        ...state,
        partialToShipment: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: {},
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENTS_STOPS_PROGRESS: {
      return {
        ...state,
        shipmentsStops: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENTS_STOPS_SUCCESS: {
      return {
        ...state,
        shipmentsStops: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENTS_STOPS_ERROR: {
      return {
        ...state,
        shipmentsStops: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_PARCEL_EXPRESS_DASHBOARD_DATA_PROGRESS: {
      return {
        ...state,
        parcelExpressDashboardData: {
          totalSpend: {
            ...state.parcelExpressDashboardData.totalSpend,
            progressing: true,
          },
          totalSpendByCarriers: {
            ...state.parcelExpressDashboardData.totalSpendByCarriers,
            progressing: true,
          },
          byMonths: {
            ...state.parcelExpressDashboardData.byMonth,
            progressing: true,
          },
        },
      };
    }
    case ShipmentTypes.GET_PARCEL_EXPRESS_DASHBOARD_DATA_SUCCESS: {
      return {
        ...state,
        parcelExpressDashboardData: {
          ...state.parcelExpressDashboardData,
          [payload.chartType]: {
            ...DEFAULT_STORE_OBJECT,
            success: true,
            ...payload,
          },
        },
      };
    }
    case ShipmentTypes.GET_PARCEL_EXPRESS_DASHBOARD_DATA_ERROR: {
      return {
        ...state,
        parcelExpressDashboardData: {
          ...state.parcelExpressDashboardData,
          [payload.chartType]: {
            ...DEFAULT_STORE_OBJECT,
            error: true,
            ...payload,
          },
        },
      };
    }

    case ShipmentTypes.SET_SHIPMENT_SEARCH_VIEW_TYPE_INITIAL:
    case ShipmentTypes.SET_SHIPMENT_SEARCH_VIEW_TYPE: {
      return {
        ...state,
        shipmentViewMode: payload,
      };
    }

    case ShipmentTypes.SET_SHIPMENT_PRODUCT_VIEW_TYPE_INITIAL:
    case ShipmentTypes.SET_SHIPMENT_PRODUCT_VIEW_TYPE: {
      return {
        ...state,
        shipmentProductViewMode: payload,
      };
    }

    case ShipmentTypes.GET_TABLE_VIEW_COLUMNS_PROGRESS: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case ShipmentTypes.GET_TABLE_VIEW_COLUMNS_SUCCESS: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GET_TABLE_VIEW_COLUMNS_ERROR: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.SAVE_TABLE_VIEW_COLUMNS_PROGRESS: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case ShipmentTypes.SAVE_TABLE_VIEW_COLUMNS_SUCCESS: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.SAVE_TABLE_VIEW_COLUMNS_ERROR: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_PRODUCT_LIST_PROGRESS: {
      const etcData =
        payload.PageNumber === 1
          ? { data: [], totalRecords: 0 }
          : {
              data: state.shipmentProductList.data,
              totalRecords: state.shipmentProductList.totalRecords,
            };

      return {
        ...state,
        shipmentProductList: {
          ...state.shipmentProductList,
          progressing: true,
          success: false,
          error: false,
          ...etcData,
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENT_PRODUCT_LIST_SUCCESS: {
      const data = payload.isSet
        ? {
            ...payload,
            data: payload.data,
          }
        : {
            ...payload,
            data: [...state.shipmentProductList.data, ...payload.data],
            totalRecords: payload.totalRecords,
          };
      return {
        ...state,
        shipmentProductList: {
          ...state.shipmentProductList,
          progressing: false,
          success: true,
          ...data,
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENT_PRODUCT_LIST_ERROR: {
      return {
        ...state,
        shipmentProductList: {
          ...state.shipmentProductList,
          progressing: false,
          error: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_SHIPMENT_PRODUCT_FULL_LIST_PROGRESS: {
      return {
        ...state,
        shipmentProductFullList: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENT_PRODUCT_FULL_LIST_SUCCESS: {
      return {
        ...state,
        shipmentProductFullList: {
          ...state.shipmentProductFullList,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.GET_SHIPMENT_PRODUCT_FULL_LIST_ERROR: {
      return {
        ...state,
        shipmentProductFullList: {
          ...state.shipmentProductFullList,
          progressing: false,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case ShipmentTypes.UPDATE_PRODUCT_LIST_PARTIAL_PROGRESS: {
      return {
        ...state,
        productPartialSave: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case ShipmentTypes.UPDATE_PRODUCT_LIST_PARTIAL_SUCCESS: {
      return {
        ...state,
        productPartialSave: {
          ...state.productPartialSave,
          progressing: false,
          success: true,
        },
      };
    }
    case ShipmentTypes.UPDATE_PRODUCT_LIST_PARTIAL_ERROR: {
      return {
        ...state,
        productPartialSave: {
          ...state.productPartialSave,
          progressing: false,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.UPDATE_FREIGHT_CLASS_PROGRESS: {
      return {
        ...state,
        updateProductFreightClass: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case ShipmentTypes.UPDATE_FREIGHT_CLASS_SUCCESS: {
      return {
        ...state,
        updateProductFreightClass: {
          ...state.updateProductFreightClass,
          progressing: false,
          success: true,
        },
        shipmentProductList: {
          ...state.shipmentProductList,
          // @ts-ignore
          data: state.shipmentProductList.data.map((row) => ({
            ...row,
            class: payload,
          })),
        },
        shipmentProductFullList: {
          ...state.shipmentProductFullList,
          // @ts-ignore
          data: state.shipmentProductFullList.data.map((row) => ({
            ...row,
            class: payload,
          })),
        },
      };
    }
    case ShipmentTypes.UPDATE_FREIGHT_CLASS_ERROR: {
      return {
        ...state,
        updateProductFreightClass: {
          ...state.updateProductFreightClass,
          progressing: false,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.UPDATE_SINGLE_PRODUCT_BY_INDEX: {
      state.shipmentProductList.data[payload.index] = {
        ...state.shipmentProductList.data[payload.index],
        ...payload.data,
      };
      state.shipmentProductFullList.data[payload.index] = {
        ...state.shipmentProductFullList.data[payload.index],
        ...payload.data,
      };
      return state;
    }

    case ShipmentTypes.UPDATE_BOL_CARRIER_PROGRESS: {
      return {
        ...state,
        updateShipmentBOLCarrier: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case ShipmentTypes.UPDATE_BOL_CARRIER_SUCCESS: {
      return {
        ...state,
        updateShipmentBOLCarrier: {
          ...state.updateShipmentBOLCarrier,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.UPDATE_BOL_CARRIER_ERROR: {
      return {
        ...state,
        updateShipmentBOLCarrier: {
          ...state.updateShipmentBOLCarrier,
          progressing: false,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_ADVANCED_PLANNING_EMAILS_PROGRESS: {
      return {
        ...state,
        getAdvancePlanningNoticeEmails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          success: false,
          error: false,
        },
      };
    }
    case ShipmentTypes.GET_ADVANCED_PLANNING_EMAILS_SUCCESS: {
      return {
        ...state,
        getAdvancePlanningNoticeEmails: {
          progressing: false,
          success: true,
          ...payload,
          // @ts-ignore
          expShipDate: normalizeESTtoUTC(payload.expShipDate),
          // @ts-ignore
          expDeliveryDate: normalizeESTtoUTC(payload.expDeliveryDate),
        },
      };
    }
    case ShipmentTypes.GET_ADVANCED_PLANNING_EMAILS_ERROR: {
      return {
        ...state,
        getAdvancePlanningNoticeEmails: {
          progressing: false,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case ShipmentTypes.SEND_ADVANCED_PLANNING_EMAIL_PROGRESS: {
      return {
        ...state,
        sendAdvancePlanningNoticeEmail: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          success: false,
          error: false,
        },
      };
    }
    case ShipmentTypes.SEND_ADVANCED_PLANNING_EMAIL_SUCCESS: {
      return {
        ...state,
        sendAdvancePlanningNoticeEmail: {
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }
    case ShipmentTypes.SEND_ADVANCED_PLANNING_EMAIL_ERROR: {
      return {
        ...state,
        sendAdvancePlanningNoticeEmail: {
          progressing: false,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case ShipmentTypes.RESET_SHIPMENT_EDIT_DETAILS: {
      return {
        ...state,
        shipmentActiveTab: '1',
        shipmentEditStatus: false,
        shipmentDetails: { ...DEFAULT_STORE_OBJECT, data: null },
        rateShopList: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          havingLeastCost: false,
        },
        documentDetails: { ...DEFAULT_STORE_OBJECT, data: [] },
        defaultAccessorials: { ...DEFAULT_STORE_OBJECT, data: [] },
        spotBidData: { ...DEFAULT_STORE_OBJECT, data: [] },
        trackingDetails: { ...DEFAULT_STORE_OBJECT, data: {} },
        shipmentCustomerAccessorials: { ...DEFAULT_STORE_OBJECT, data: [] },
        cargoSpecificAccessorials: { ...DEFAULT_STORE_OBJECT, data: [] },
        masterChildDetails: { ...DEFAULT_STORE_OBJECT, data: [] },
        shipmentProductList: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          totalRecords: 0,
          PageSize: 10,
          PageNumber: 1,
        },
        shipmentProductFullList: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
        },
        endSpotBid: { ...DEFAULT_STORE_OBJECT },
      };
    }

    case ShipmentTypes.SET_EDIT_SHIPMENT_RATESHOP_MODAL: {
      return {
        ...state,
        shipmentEditRateShopModal: payload,
      };
    }
    case ShipmentTypes.SET_SELECTED_RATE_SHOP_INDEX: {
      return {
        ...state,
        selectedRateShopIndex: payload,
      };
    }

    // todo refactor it (SPOT QUOTE STATE)

    case ShipmentTypes.GET_SPOT_QUOTE_BIDDERS_PROGRESS: {
      return {
        ...state,
        spotQuoteBidders: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_SPOT_QUOTE_BIDDERS_SUCCESS: {
      return {
        ...state,
        spotQuoteBidders: {
          ...state.spotQuoteBidders,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SPOT_QUOTE_BIDDERS_ERROR: {
      return {
        ...state,
        spotQuoteBidders: {
          ...state.spotQuoteBidders,
          progressing: false,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SPOT_QUOTE_CARGO_TYPES_PROGRESS: {
      return {
        ...state,
        cargoTypes: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_SPOT_QUOTE_CARGO_TYPES_SUCCESS: {
      return {
        ...state,
        cargoTypes: {
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SPOT_QUOTE_CARGO_TYPES_ERROR: {
      return {
        ...state,
        cargoTypes: {
          progressing: false,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case ShipmentTypes.SEARCH_SPOT_QUOTE_BIDDERS_PROGRESS: {
      return {
        ...state,
        searchableSpotQuoteBidders: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.SEARCH_SPOT_QUOTE_BIDDERS_SUCCESS: {
      return {
        ...state,
        searchableSpotQuoteBidders: {
          ...state.spotQuoteBidders,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.SEARCH_SPOT_QUOTE_BIDDERS_ERROR: {
      return {
        ...state,
        searchableSpotQuoteBidders: {
          ...state.spotQuoteBidders,
          progressing: false,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case ShipmentTypes.RESET_SEARCH_QUOTE_BIDDERS: {
      return {
        ...state,
        searchableSpotQuoteBidders: {
          ...state.spotQuoteBidders,
          ...DEFAULT_STORE_OBJECT,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_SPOT_QUOTE_COLLABORATORS_PROGRESS: {
      return {
        ...state,
        spotQuoteCollaborators: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case ShipmentTypes.GET_SPOT_QUOTE_COLLABORATORS_SUCCESS: {
      return {
        ...state,
        spotQuoteCollaborators: {
          ...state.spotQuoteCollaborators,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_SPOT_QUOTE_COLLABORATORS_ERROR: {
      return {
        ...state,
        spotQuoteCollaborators: {
          ...state.spotQuoteCollaborators,
          progressing: false,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case ShipmentTypes.DELETE_SHIPMENT_TENDER_PROGRESS: {
      return {
        ...state,
        deleteShipmentTenderProcess: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case ShipmentTypes.DELETE_SHIPMENT_TENDER_SUCCESS: {
      return {
        ...state,
        deleteShipmentTenderProcess: {
          ...state.deleteShipmentTenderProcess,
          progressing: false,
          success: true,
        },
      };
    }
    case ShipmentTypes.DELETE_SHIPMENT_TENDER_ERROR: {
      return {
        ...state,
        deleteShipmentTenderProcess: {
          ...state.deleteShipmentTenderProcess,
          progressing: false,
          error: true,
          ...payload,
        },
      };
    }

    case ShipmentTypes.GET_CARGO_SPECIFIC_ACCESSORIALS_PROGRESS: {
      return {
        ...state,
        cargoSpecificAccessorials: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }
    case ShipmentTypes.GET_CARGO_SPECIFIC_ACCESSORIALS_SUCCESS: {
      return {
        ...state,
        cargoSpecificAccessorials: {
          ...state.cargoSpecificAccessorials,
          progressing: false,
          success: true,
          data: payload.data,
        },
      };
    }
    case ShipmentTypes.GET_CARGO_SPECIFIC_ACCESSORIALS_ERROR: {
      return {
        ...state,
        cargoSpecificAccessorials: {
          ...state.cargoSpecificAccessorials,
          progressing: false,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    default:
      return state;
  }
};

export default ShipmentReducer;
